import Helper from '@ember/component/helper';

type Positional = [schoolId: string, currentUrl: string | null];

export interface UrlForExistingScopeSignature {
  Args: {
    Positional: Positional;
  };
  Return: string;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'url-for-existing-scope': typeof UrlForExistingScope;
  }
}

/**
 * Build a URL that can be linked to containing a new School ID given the
 * current route and URL. The main intention here is to enable switching
 * between schools while maintaining the existing viewing context.
 *
 * For example, if an admin is viewing `/a/123/analytics`, all
 * the links in the school selector should replace the school ID while
 * maintaining the context like so: `/a/[NEW_ID]/analytics`.
 *
 * Given a current school ID of `123` and a new school ID of 789, here are
 * some basic examples:
 *
 * `/a/123/teachers`             => `/a/789/teachers`
 * `/a/123/teachers/pending`     => `/a/789/teachers`
 * `/a/123/analytics/overview`   => `/a/789/analytics`
 *
 * Ideally, we would keep legitimate nested sections instead of hoisting the
 * user back out to a known level (see example below) but this is non-trivial
 * at the moment so we're settling for landing on the `/teachers` section.
 *
 *  *** 🔻 NOT THE CURRENT IMPLEMENTATION ***
 * `/a/123/teachers/pending`     => `/a/789/teachers/pending`
 * `/a/123/analytics/overview`   => `/a/789/analytics/overview`
 *  *** 🔺 NOT THE CURRENT IMPLEMENTATION ***
 *
 * Note that certain routes in the app require a bit more work. Namely, all
 * teacher IDs (including any route path afterwards) are stripped from the
 * resulting URL. This is necessary because a specific teacher ID belongs to
 * one school and does not make sense in the context of another school.
 * For example:
 *
 * `/a/123/teachers/456/courses` => `/a/789/teachers`
 */
export default class UrlForExistingScope extends Helper<UrlForExistingScopeSignature> {
  public compute([schoolId, currentURL]: Positional) {
    const updatedURL =
      currentURL?.replace(/\/a\/([\w\d]+)/, `/a/${schoolId}`) ?? '';

    /**
     * Test for specific nested route level.
     * See docs above for details.
     */
    const match = /\/a\/[\w\d]+\/(teachers|analytics)(?:\/.+)/.exec(updatedURL);
    if (match) {
      return `/a/${schoolId}/${match[1]}`;
    }

    return updatedURL ?? '#';
  }
}
